import { useMemo, useEffect } from 'react';
import { MetaPage } from '../types/meta.types';
import { Facebook, Search, ExternalLink, Settings, RefreshCw, Trash2, TriangleAlert, Boxes, UserCircle, Edit3, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BulkUpdatePagesDialog from './BulkUpdatePagesDialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Mensagens amigáveis pros códigos de erro do Meta que vemos com frequência.
// Subcode é opcional — se bater, mostra mensagem mais específica.
function describeMetaError(code: number | null | undefined, subcode: number | null | undefined): string {
    if (!code) return 'Token inválido (sem código). Reconecte a página.';
    if (code === 190) {
        if (subcode === 459) return 'Checkpoint da conta no Facebook. Peça pro dono fazer login no facebook.com e completar a verificação.';
        if (subcode === 464) return 'Conta do dono caiu em "user not confirmed". Peça pro dono fazer login e confirmar a conta no facebook.com.';
        if (subcode === 458) return 'Senha do dono foi alterada. Reconecte o app.';
        if (subcode === 460) return 'Sessão expirou. Reconecte o app.';
        if (subcode === 467) return 'Token expirou (inativo > 60 dias). Reconecte o app.';
        return 'Token de acesso inválido ou expirado. Reconecte o app.';
    }
    if (code === 200) return 'Sem permissão pra usar essa página. Verifique os roles no app.';
    if (code === 2022) return 'Bloqueio temporário por conteúdo (#2022). A página enviou algo que o Meta marcou como violação.';
    if (code === 4) return 'Limite de requisições atingido. Tenta de novo em alguns minutos.';
    if (code === 10) return 'Janela de 24h fechada. Só permitido mensagens iniciadas pelo lead.';
    if (code === 551) return 'Destinatário indisponível.';
    return `Erro Meta #${code}${subcode ? ` (sub: ${subcode})` : ''}.`;
}


interface PagesListProps {
    pages: MetaPage[];
    onToggleStatus: (page: MetaPage) => void;
    onConfigure: (page: MetaPage) => void;
    onSyncLeads?: (page: MetaPage) => void;
    onConnect?: () => void;
    onDeletePage?: (page: MetaPage) => void;
}

export default function PagesList({ pages, onToggleStatus: _onToggleStatus, onConfigure: _onConfigure, onSyncLeads, onConnect, onDeletePage }: PagesListProps) {
    // Seleção múltipla pra bulk update — estado por id (estável entre re-renders
    // mesmo se a lista mudar). Limpa ao trocar de filtro pra evitar sumir item
    // selecionado sem feedback visual.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState('all');  // 'all' | 'none' | groupId
    const [selectedMetaUser, setSelectedMetaUser] = useState('all');  // 'all' | meta_user_name
    const [onlyWithWarn, setOnlyWithWarn] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;
    const navigate = useNavigate();

    // Build unique category options from actual page data
    const categoryOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => { if (p.internal_category_name) unique.add(p.internal_category_name); });
        return Array.from(unique).sort();
    }, [pages]);

    // Unique group options (id → name) from page data
    const groupOptions = useMemo(() => {
        const map = new Map<number, string>();
        pages.forEach(p => {
            if (p.internal_group_id && p.internal_group_name) {
                map.set(p.internal_group_id, p.internal_group_name);
            }
        });
        return Array.from(map.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [pages]);

    // Unique meta_user (conta do Facebook que vinculou a página) options.
    // Agrupa por `accessible_meta_user_names` (master + page_tokens ativos)
    // pra incluir dual-app. Fallback pra `meta_user_name` (master) se o backend
    // não enriqueceu — mantém compat enquanto a build do back não está no ar.
    const metaUserOptions = useMemo(() => {
        const unique = new Set<string>();
        pages.forEach(p => {
            const names = p.accessible_meta_user_names && p.accessible_meta_user_names.length > 0
                ? p.accessible_meta_user_names
                : (p.meta_user_name ? [p.meta_user_name] : []);
            names.forEach(n => { if (n) unique.add(n); });
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [pages]);

    // Filtro client-side (busca + categoria + grupo + meta_user + warn)
    const filteredPages = pages.filter(page => {
        const matchesSearch = !searchTerm || page.page_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || page.internal_category_name === selectedCategory;
        const matchesGroup =
            selectedGroup === 'all' ? true :
            selectedGroup === 'none' ? !page.internal_group_id :
            String(page.internal_group_id) === selectedGroup;
        // Match contra accessible_meta_user_names (inclui dual-app); fallback
        // pra meta_user_name (master) quando o backend não enriqueceu ainda.
        const accessibleNames = page.accessible_meta_user_names && page.accessible_meta_user_names.length > 0
            ? page.accessible_meta_user_names
            : (page.meta_user_name ? [page.meta_user_name] : []);
        const matchesMetaUser = selectedMetaUser === 'all' || accessibleNames.includes(selectedMetaUser);
        const matchesWarn = !onlyWithWarn || (page.token_status && page.token_status !== 'ok');
        return matchesSearch && matchesCategory && matchesGroup && matchesMetaUser && matchesWarn;
    });

    // Paginação client-side
    const totalPages = Math.ceil(filteredPages.length / perPage);
    const paginatedPages = filteredPages.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Reset página quando filtro muda + limpa seleção (evita sumir item selecionado
    // sem feedback visual quando o user troca filtro).
    useEffect(() => {
        setCurrentPage(1);
        setSelectedIds(new Set());
    }, [searchTerm, selectedCategory, selectedGroup, selectedMetaUser, onlyWithWarn]);

    // Estado do "selecionar todos" da página atual (paginada).
    // - true  = todas as visíveis estão marcadas
    // - false = nenhuma
    // - 'indeterminate' = algumas (renderizado igual false pelo shadcn)
    const allVisibleSelected = paginatedPages.length > 0
        && paginatedPages.every(p => selectedIds.has(p.id));
    const someVisibleSelected = paginatedPages.some(p => selectedIds.has(p.id));

    const toggleSelectAllVisible = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                paginatedPages.forEach(p => next.delete(p.id));
            } else {
                paginatedPages.forEach(p => next.add(p.id));
            }
            return next;
        });
    };

    const toggleSelectOne = (pageId: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(pageId)) next.delete(pageId);
            else next.add(pageId);
            return next;
        });
    };

    const clearSelection = () => setSelectedIds(new Set());

    if (pages.length === 0) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Páginas</h1>
                        <p className="mt-2 text-gray-600">Gerencie as páginas vinculadas ao Meta Ads.</p>
                    </div>
                    <Button
                        onClick={onConnect}
                        className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                    >
                        <Facebook className="mr-2 h-4 w-4 fill-white" />
                        Vincular Página
                    </Button>
                </div>

                <div className="bg-white rounded-lg p-16 text-center shadow border border-gray-100">
                    <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-6">
                        <Facebook className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Nenhuma página vinculada
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                        Conecte sua conta do Facebook para começar a gerenciar suas páginas.
                    </p>
                    <Button onClick={onConnect} variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50">
                        Conectar Agora
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Páginas</h1>
                    <p className="mt-2 text-gray-600">Gerencie suas páginas vinculadas e configurações.</p>
                </div>
                <Button
                    onClick={onConnect}
                    className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                >
                    <Facebook className="mr-2 h-4 w-4 fill-white" />
                    Vincular Página
                </Button>
            </div>

            {/* Filter Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Buscar por nome
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Página..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1">
                            Categoria
                        </label>
                        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                            <SelectTrigger>
                                <SelectValue placeholder="Todas categorias" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas categorias</SelectItem>
                                {categoryOptions.map((cat) => (
                                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                            <Boxes className="h-3 w-3" />
                            Grupo
                        </label>
                        <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                            <SelectTrigger>
                                <SelectValue placeholder="Todos grupos" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos grupos</SelectItem>
                                <SelectItem value="none">Sem grupo</SelectItem>
                                {groupOptions.map((g) => (
                                    <SelectItem key={g.id} value={String(g.id)}>{g.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex-1 min-w-48 space-y-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider ml-1 flex items-center gap-1">
                            <UserCircle className="h-3 w-3" />
                            Conta Meta
                        </label>
                        <Select value={selectedMetaUser} onValueChange={setSelectedMetaUser}>
                            <SelectTrigger>
                                <SelectValue placeholder="Todas contas" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todas contas</SelectItem>
                                {metaUserOptions.map((name) => (
                                    <SelectItem key={name} value={name}>{name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer pb-2">
                        <div
                            onClick={() => setOnlyWithWarn(v => !v)}
                            className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${onlyWithWarn ? 'bg-red-500' : 'bg-gray-200'}`}
                        >
                            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${onlyWithWarn ? 'translate-x-4' : 'translate-x-0'}`} />
                        </div>
                        <div className="flex items-center gap-1.5">
                            <TriangleAlert className={`h-3.5 w-3.5 ${onlyWithWarn ? 'text-red-500' : 'text-gray-400'}`} />
                            <span className="text-sm text-gray-600 whitespace-nowrap">Com problema</span>
                        </div>
                    </label>
                </div>
            </div>

            {/* Table Header Stats */}
            <div className="px-1">
                <span className="text-sm text-gray-500">
                    Mostrando <strong className="text-gray-900">{paginatedPages.length}</strong> de <strong className="text-gray-900">{filteredPages.length}</strong> páginas
                </span>
            </div>

            {/* Barra de ação flutuante — aparece quando ≥1 página selecionada.
                Mostra contagem + botões "Atualizar em massa" e "Limpar seleção". */}
            {selectedIds.size > 0 && (
                <div className="sticky top-2 z-20 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-blue-600 text-white border-blue-600 font-semibold">
                            {selectedIds.size}
                        </Badge>
                        <span className="text-sm text-blue-900 font-medium">
                            {selectedIds.size === 1 ? 'página selecionada' : 'páginas selecionadas'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={clearSelection}
                            className="text-gray-600 hover:bg-white"
                        >
                            <X className="h-4 w-4 mr-1" />
                            Limpar
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => setBulkDialogOpen(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            <Edit3 className="h-4 w-4 mr-2" />
                            Atualizar em massa
                        </Button>
                    </div>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-white rounded-lg shadow border border-gray-100 overflow-x-auto w-full">
                <table className="w-full divide-y divide-gray-200 table-fixed" style={{ minWidth: '940px' }}>
                    <colgroup>
                        <col style={{ width: '40px' }} />
                        <col style={{ width: '28%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '30%' }} />
                    </colgroup>
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="pl-6 pr-2 py-3">
                                <Checkbox
                                    checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                                    onCheckedChange={toggleSelectAllVisible}
                                    aria-label="Selecionar todas visíveis"
                                />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Página
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Categoria
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                País
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Ações
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedPages.map((page) => (
                            <tr key={page.id} className={`transition-colors ${selectedIds.has(page.id) ? 'bg-blue-50/40 hover:bg-blue-50/60' : 'hover:bg-gray-50'}`}>
                                <td className="pl-6 pr-2 py-4">
                                    <Checkbox
                                        checked={selectedIds.has(page.id)}
                                        onCheckedChange={() => toggleSelectOne(page.id)}
                                        aria-label={`Selecionar ${page.page_name}`}
                                    />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-4">
                                        <div className="relative">
                                            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                                                <Facebook className="h-5 w-5 text-blue-600" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-gray-900 leading-none">
                                                    {page.page_name}
                                                </p>
                                                <a
                                                    href={`https://facebook.com/${page.page_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-gray-400 hover:text-blue-600"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                                {page.token_status && page.token_status !== 'ok' && (
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <button
                                                                type="button"
                                                                title="Ver detalhes do problema"
                                                                className="shrink-0 rounded-sm hover:bg-red-50 transition-colors p-0.5 -m-0.5"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <TriangleAlert className="h-3.5 w-3.5 text-red-500" />
                                                            </button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            align="start"
                                                            className="w-80 p-0 overflow-hidden"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
                                                                <TriangleAlert className="h-4 w-4 text-red-600" />
                                                                <span className="text-sm font-semibold text-red-900">Problema no token</span>
                                                            </div>
                                                            <div className="p-3 space-y-3 max-h-72 overflow-y-auto">
                                                                {(page.apps && page.apps.length > 0
                                                                    ? page.apps.filter(a => a.token_status && a.token_status !== 'ok')
                                                                    : []
                                                                ).map((a) => (
                                                                    <div key={a.app_id_pk} className="space-y-1">
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <span className="text-xs font-medium text-gray-900 truncate">{a.app_name}</span>
                                                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                                                                                {a.token_status}
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-xs text-gray-600 leading-snug">
                                                                            {describeMetaError(a.token_error_code, a.token_error_subcode)}
                                                                        </p>
                                                                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                                                            {a.token_error_code != null && (
                                                                                <span>code {a.token_error_code}{a.token_error_subcode != null ? `/${a.token_error_subcode}` : ''}</span>
                                                                            )}
                                                                            {a.token_checked_at && (
                                                                                <span>· checado {new Date(a.token_checked_at).toLocaleString('pt-BR')}</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                {(!page.apps || page.apps.filter(a => a.token_status && a.token_status !== 'ok').length === 0) && (
                                                                    <p className="text-xs text-gray-600">
                                                                        Status agregado: <span className="font-mono">{page.token_status}</span>
                                                                        {page.token_checked_at && (
                                                                            <span className="text-gray-400"> · checado {new Date(page.token_checked_at).toLocaleString('pt-BR')}</span>
                                                                        )}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">ID: {page.page_id}</p>
                                            {page.meta_user_name && (
                                                <p className="text-xs text-gray-400 mt-0.5">{page.meta_user_name}</p>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex flex-col gap-1">
                                        {page.internal_category_name ? (
                                            <Badge variant="outline" className="font-medium text-blue-600 border-blue-100 bg-blue-50/30 w-fit">
                                                {page.internal_category_name}
                                            </Badge>
                                        ) : (
                                            <span className="text-sm text-gray-400">-</span>
                                        )}
                                        {page.internal_group_name && (
                                            <Badge variant="outline" className="font-medium text-indigo-700 border-indigo-200 bg-indigo-50/40 w-fit flex items-center gap-1">
                                                <Boxes className="h-3 w-3" />
                                                {page.internal_group_name}
                                                {page.internal_group_app_name && (
                                                    <span className="text-indigo-500 ml-0.5">→ {page.internal_group_app_name}</span>
                                                )}
                                            </Badge>
                                        )}
                                        {(page.apps && page.apps.length > 1) && (
                                            <span className="text-[10px] text-gray-500" title={page.apps.map(a => a.app_name).join(', ')}>
                                                {page.apps.length} apps conectados
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-700">
                                        {page.language ? page.language.split(' - ')[1] ?? page.language : '-'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {page.is_active ? (
                                        <Badge variant="success">Ativa</Badge>
                                    ) : (
                                        <Badge variant="destructive">Inativa</Badge>
                                    )}
                                </td>
                                <td className="px-4 py-4 text-right whitespace-nowrap w-px">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onSyncLeads?.(page)}
                                            className="h-9 px-3 text-emerald-600 hover:bg-emerald-50"
                                        >
                                            <RefreshCw className="h-4 w-4 mr-2" />
                                            Sync Leads
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate(`/meta/connection/pages/${page.id}`)}
                                            className="h-9 px-3 text-blue-600 hover:bg-blue-50"
                                        >
                                            <Settings className="h-4 w-4 mr-2" />
                                            Detalhes
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onDeletePage?.(page)}
                                            className="h-9 px-3 text-red-600 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-1">
                    <span className="text-sm text-gray-500">
                        Página {currentPage} de {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage <= 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                        >
                            Anterior
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={currentPage >= totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                        >
                            Próxima
                        </Button>
                    </div>
                </div>
            )}

            <BulkUpdatePagesDialog
                open={bulkDialogOpen}
                onOpenChange={setBulkDialogOpen}
                pageIds={Array.from(selectedIds)}
                onSuccess={clearSelection}
            />
        </div>
    );
}